"use client"

import type React from "react"
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "../components/ui/dialog"
import type { ResultFormData } from "../components/types/application.types"

interface ResultModalProps {
  isOpen: boolean
  onClose: () => void
  resultFormData: ResultFormData
  setResultFormData: React.Dispatch<React.SetStateAction<ResultFormData>>
  isSubmittingResult: boolean
  onSubmit: () => void
}

export function ResultModal({
  isOpen,
  onClose,
  resultFormData,
  setResultFormData,
  isSubmittingResult,
  onSubmit,
}: ResultModalProps) {
  const todayISO = new Date().toISOString().split("T")[0]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md sm:max-w-lg rounded-xl border border-gray-300 bg-white shadow-lg p-4 sm:p-6">
        <DialogHeader className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center ">
            {resultFormData.result === "Qualified" ? (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Qualified Candidate</h3>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Reject Candidate</h3>
              </>
            )}
          </div>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Description */}
          <div className="relative">
            <textarea
              id="description"
              placeholder={
                resultFormData.result === "Qualified"
                  ? "Provide feedback on why this candidate is qualified..."
                  : "Provide feedback on why this candidate is being rejected..."
              }
              value={resultFormData.reason}
              onChange={(e) => setResultFormData((prev) => ({ ...prev, reason: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 h-20 resize-none"
              required
            />
          </div>

          {/* Final Round Checkbox - only for Qualified */}
          {resultFormData.result === "Qualified" && (
            <>
              <div className="flex items-center gap-2">
                <input
                  id="finalRound"
                  type="checkbox"
                  checked={resultFormData.isFinalRound}
                  onChange={(e) =>
                    setResultFormData((prev) => ({
                      ...prev,
                      isFinalRound: e.target.checked,
                      ...(e.target.checked ? {} : { expectedJoiningDate: "" }),
                    }))
                  }
                  className="h-4 w-4 accent-orange-600 border-gray-200 rounded focus:ring-orange-500"
                />
                <label htmlFor="finalRound" className="text-sm text-gray-700">
                  Final Round
                </label>
              </div>

              {/* Expected Joining Date - only when Final Round is checked */}
              {resultFormData.isFinalRound && (
                <div className="space-y-1">
                  <label htmlFor="expectedJoiningDate" className="text-sm text-gray-700">
                    Expected Joining Date (Optional)
                  </label>
                  <input
                    id="expectedJoiningDate"
                    type="date"
                    value={resultFormData.expectedJoiningDate || ""}
                    min={todayISO}
                    onChange={(e) =>
                      setResultFormData((prev) => ({ ...prev, expectedJoiningDate: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    required
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          {resultFormData.result === "Qualified" ? (
            <button
              onClick={onSubmit}
              disabled={isSubmittingResult || !resultFormData.reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmittingResult ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Qualify Candidate
                </>
              )}
            </button>
          ) : (
            <button
              onClick={onSubmit}
              disabled={isSubmittingResult || !resultFormData.reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmittingResult ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  Reject Candidate
                </>
              )}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
